import React, { useEffect, useState } from "react";
import Menu from "../panel/Menu";
import {
    Box, Card, CardContent, CardHeader,
    Typography, Stack, Divider,
    TextField, IconButton, Grid, Chip
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import {
    collection, getDocs, addDoc,
    updateDoc, deleteDoc, doc
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../firebase";
import Swal from "sweetalert2";

function Amenities() {
    const auth = getAuth();

    const [categories, setCategories] = useState([]);
    const [newCategory, setNewCategory] = useState("");
    const [newItem, setNewItem] = useState({});
    const [loading, setLoading] = useState(true);

    const [userData, setUserData] = useState(null);
    const [propertyId, setPropertyId] = useState(null);

    /* ================= AUTH ================= */
    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (user) {
                setUserData(user);

                // get propertyId from authroles
                const roleSnap = await getDocs(collection(db, "authroles"));
                roleSnap.forEach(d => {
                    if (d.id === user.uid) {
                        setPropertyId(d.data().propertyId);
                    }
                });
            }
        });

        return () => unsub();
    }, []);

    /* ================= FETCH ================= */
    const fetchCategories = async () => {
        if (!userData || !propertyId) return;

        const snap = await getDocs(collection(db, "amenity_categories"));

        const list = snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(
                c => c.userId === userData.uid && c.propertyId === propertyId
            );

        setCategories(list);
        setLoading(false);
    };

    useEffect(() => {
        fetchCategories();
    }, [userData, propertyId]);

    /* ================= ADD CATEGORY ================= */
    const addCategory = async () => {
        if (!newCategory.trim() || !userData || !propertyId) return;

        await addDoc(collection(db, "amenity_categories"), {
            title: newCategory.trim(),
            items: [],
            userId: userData.uid,
            propertyId: propertyId,
            createdAt: new Date()
        });

        Swal.fire("Added", "Amenity category created", "success");
        setNewCategory("");
        fetchCategories();
    };

    /* ================= DELETE CATEGORY ================= */
    const deleteCategory = async (id) => {
        await deleteDoc(doc(db, "amenity_categories", id));
        Swal.fire("Deleted", "Category removed", "success");
        fetchCategories();
    };

    /* ================= ADD SUB AMENITY ================= */
    const addSubAmenity = async (cat) => {
        const value = newItem[cat.id];
        if (!value?.trim()) return;

        const updated = [...(cat.items || []), value.trim()];

        await updateDoc(doc(db, "amenity_categories", cat.id), {
            items: updated
        });

        setNewItem({ ...newItem, [cat.id]: "" });
        fetchCategories();
    };

    /* ================= DELETE SUB AMENITY ================= */
    const deleteSubAmenity = async (cat, item) => {
        const updated = cat.items.filter(i => i !== item);

        await updateDoc(doc(db, "amenity_categories", cat.id), {
            items: updated
        });

        fetchCategories();
    };

    return (
        <Box display="flex" minHeight="100vh" bgcolor="#f4f6f8">
            <Menu />

            <Box flex={1} p={3} className="content-wrapper">
                <Typography variant="h5" fontWeight={700} mb={3}>
                    Amenities Master Management
                </Typography>

                {/* ================= ADD MAIN CATEGORY ================= */}
                <Card sx={{ mb: 4, borderRadius: 3 }}>
                    {/* <CardHeader title="Add Main Amenity Type" /> */}
                    <Divider />
                    <CardContent>
                        <Box display="flex" gap={1}>
                            <TextField
                                fullWidth
                                label="Room Amenities / Hotel Amenities"
                                size="small"
                                value={newCategory}
                                onChange={(e) => setNewCategory(e.target.value)}
                            />
                            <IconButton color="primary" onClick={addCategory}>
                                <Add />
                            </IconButton>
                        </Box>
                    </CardContent>
                </Card>

                {/* ================= CATEGORY LIST ================= */}
                {loading && <Typography>Loading...</Typography>}

                <Grid className="row" spacing={3}>
                    {categories.map(cat => (
                        <Grid item className="col-sm-4 mb-3" xs={12} md={4} key={cat.id}>
                            <Card
                                sx={{
                                    borderRadius: 3,
                                    height: "100%",
                                    boxShadow: "0 6px 20px rgba(0,0,0,0.08)"
                                }}
                            >
                                <CardHeader
                                    title={cat.title}
                                    action={
                                        <IconButton
                                            onClick={() => deleteCategory(cat.id)}
                                            color="error"
                                        >
                                            <Delete />
                                        </IconButton>
                                    }
                                />
                                <Divider />

                                <CardContent>
                                    {/* Add Sub Amenity */}
                                    <Box display="flex" gap={1} mb={2}>
                                        <TextField
                                            size="small"
                                            label="Add Sub Amenity"
                                            value={newItem[cat.id] || ""}
                                            onChange={(e) =>
                                                setNewItem({ ...newItem, [cat.id]: e.target.value })
                                            }
                                            fullWidth
                                        />
                                        <IconButton
                                            color="primary"
                                            onClick={() => addSubAmenity(cat)}
                                        >
                                            <Add />
                                        </IconButton>
                                    </Box>

                                    {/* Sub Amenities */}
                                    <Stack direction="row" flexWrap="wrap" gap={1}>
                                        {(cat.items || []).map(item => (
                                            <Chip
                                                key={item}
                                                label={item}
                                                onDelete={() => deleteSubAmenity(cat, item)}
                                            />
                                        ))}
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Box>
    );
}

export default Amenities;
